import { styled } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
import { Box, Typography, keyframes } from '@mui/material'

const LEARN_COLOR = '#55C1E2'
const COMPLETED_GREEN = '#43a047'
const GOLD = '#C9A227'

export const TriggerButton = styled('button')({
	background: 'transparent',
	border: 0,
	cursor: 'pointer',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: 48,
	height: 48,
	padding: 0,

	'&:focus-visible': {
		outline: `2px solid ${LEARN_COLOR}`,
		outlineOffset: 2,
		borderRadius: 4,
	},
})

export const BrainIcon = styled('img')({
	width: 36,
	height: 36,
	display: 'block',
})

// Sits in normal flow above the panel content, sharing ContentWrapper's
// max-width + centering so the close button lines up with the content
// column's right edge instead of the panel's outer (viewport-wide) edge.
export const PanelTopBar = styled(Box)({
	position: 'relative',
	width: '100%',
	maxWidth: '960px',
	margin: '0 auto',
	display: 'flex',
	justifyContent: 'center',
})

export const PanelCloseButton = styled('button')({
	flexShrink: 0,
	background: 'transparent',
	border: 0,
	cursor: 'pointer',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: 32,
	height: 32,
	padding: 0,
	borderRadius: '50%',
	color: 'rgba(0, 0, 0, 0.5)',
	transition: 'background 0.15s ease, color 0.15s ease',

	'& svg': {
		fontSize: 20,
	},

	'&:hover': {
		background: 'rgba(0, 0, 0, 0.06)',
		color: 'rgba(0, 0, 0, 0.75)',
	},

	'&:focus-visible': {
		outline: `2px solid ${LEARN_COLOR}`,
		outlineOffset: 2,
	},
})

const PANEL_MAX_HEIGHT = 'min(540px, calc(100vh - 80px))'

// `compact` is set for the "ready" (has a learning path) state: its content is
// shorter than the setup wizard's, so the panel shrinks to fit it instead of
// reserving the wizard's full height. PanelBody's flex:1 + overflowY: auto
// still lets it scroll internally if content ever exceeds the max height.
export const Panel = styled(Box, {
	shouldForwardProp: prop => prop !== 'compact',
})<{ compact?: boolean }>(({ compact }) => ({
	position: 'fixed',
	top: '80px',
	left: 0,
	right: 0,
	width: '100%',
	height: compact ? 'auto' : PANEL_MAX_HEIGHT,
	minHeight: compact ? '220px' : undefined,
	maxHeight: PANEL_MAX_HEIGHT,
	display: 'flex',
	flexDirection: 'column',
	overflow: 'hidden',
	background: '#fff',
	boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
	borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
	padding: compact ? '24px 24px' : '32px 24px',
	transform: 'translateY(-100%)',
	visibility: 'hidden',
	pointerEvents: 'none',
	transition: 'transform 0.3s ease, visibility 0.3s',
	zIndex: 1400,

	'&.state-visible': {
		transform: 'translateY(0)',
		visibility: 'visible',
		pointerEvents: 'auto',
	},
}))

export const ContentWrapper = styled(Box)({
	width: '100%',
	maxWidth: '960px',
	margin: '0 auto',
	flex: 1,
	minHeight: 0,
	display: 'flex',
	flexDirection: 'column',
})

// Keep scrolled content (e.g. the bordered recommendation box) clear of the
// scrollbar/overlay so its border never sits underneath it on narrow panels.
const SCROLL_GUTTER = 8

export const PanelBody = styled(Box)({
	flex: 1,
	marginTop: 1,
	minHeight: 0,
	overflowY: 'auto',
	paddingRight: `${SCROLL_GUTTER}px`,
})

export const PanelCenter = styled(Box)({
	flex: 1,
	minHeight: 0,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
})

export const HeaderRow = styled(Box)({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	flexWrap: 'wrap',
	gap: '12px',
	marginBottom: '20px',
	// Cancel PanelBody's scroll gutter so the close button below, centered via
	// left: 50% against this row, lines up with the brain icon toggle above the
	// panel instead of drifting off-centre by half the gutter width.
	marginRight: `-${SCROLL_GUTTER}px`,
})

export const MentorIdentity = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: '12px',
})

export const MentorAvatar = styled('img')({
	width: 44,
	height: 44,
	display: 'block',
})

export const MentorName = styled(Typography)({
	fontWeight: 600,
	fontSize: '1.25rem',
	lineHeight: 1.2,
	color: 'rgba(0, 0, 0, 0.87)',
})

export const MentorTitle = styled(Typography)({
	fontSize: '0.85rem',
	color: 'rgba(0, 0, 0, 0.6)',
})

export const ScoreRankRow = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: '10px',
	flexWrap: 'wrap',
})

export const HeaderActions = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: '10px',
	marginLeft: 'auto',
})

export const HeaderCloseAnchor = styled(Box)({
	position: 'absolute',
	top: 0,
	left: '50%',
	transform: 'translateX(-50%)',
})

export const StatPill = styled(Box)({
	display: 'inline-flex',
	alignItems: 'center',
	gap: '6px',
	padding: '6px 12px',
	borderRadius: '999px',
	background: 'rgba(0, 0, 0, 0.05)',
	color: 'rgba(0, 0, 0, 0.75)',
	fontSize: '0.85rem',
	fontWeight: 600,

	'& svg': {
		fontSize: 18,
		color: LEARN_COLOR,
	},
})

export const SectionLabel = styled(Typography)({
	fontSize: '0.7rem',
	fontWeight: 700,
	letterSpacing: '0.08em',
	textTransform: 'uppercase',
	color: 'rgba(0, 0, 0, 0.45)',
	marginBottom: '10px',
})

export const OverallJustification = styled(Typography)({
	fontSize: '0.85rem',
	color: 'rgba(0, 0, 0, 0.6)',
	lineHeight: 1.5,
	marginBottom: '14px',
})

export type TimelineNodeVariant =
	'completed' | 'current' | 'upcoming' | 'achievement'
export type ConnectorColor = 'green' | 'grey' | 'hidden' | 'dotted'

const NODE_SIZE = 30
const CONNECTOR_WIDTH = 2

// The horizontal path preview runs out of room and starts to scroll before the
// wizard forms do, so it stacks vertically at a wider viewport than the `sm`
// breakpoint (600px) that governs the rest of the panel chrome.
const PATH_STACK_MAX_WIDTH = 695

const DOTTED_LINE = (theme: Theme) =>
	`repeating-linear-gradient(to right, ${theme.palette.grey[400]} 0 5px, transparent 5px 9px)`

const DOTTED_LINE_VERTICAL = (theme: Theme) =>
	`repeating-linear-gradient(to bottom, ${theme.palette.grey[400]} 0 5px, transparent 5px 9px)`

const connectorStyle = (theme: Theme, color: ConnectorColor) => {
	if (color === 'green') return { background: COMPLETED_GREEN }
	if (color === 'grey') return { background: theme.palette.grey[300] }
	if (color === 'dotted') return { backgroundImage: DOTTED_LINE(theme) }
	return { background: 'transparent' }
}

export const PathRow = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'flex-start',
	overflowX: 'auto',
	// Setting overflowX alone computes overflowY to 'auto' too (per the CSS
	// overflow spec), which clips anything above the row's top edge — namely
	// the pick indicator, which pokes a few px above its node. paddingTop
	// gives it room instead of leaving that unset (== 0).
	paddingTop: '6px',
	paddingBottom: '8px',

	'&::-webkit-scrollbar': {
		height: '6px',
	},
	'&::-webkit-scrollbar-thumb': {
		background: 'rgba(0, 0, 0, 0.15)',
		borderRadius: '3px',
	},

	[theme.breakpoints.down(PATH_STACK_MAX_WIDTH)]: {
		flexDirection: 'column',
		alignItems: 'stretch',
		overflowX: 'visible',
		paddingBottom: 0,
	},
}))

export const TimelineItem = styled(Box)(({ theme }) => ({
	position: 'relative',
	flex: '1 1 0',
	minWidth: 116,
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',

	[theme.breakpoints.down(PATH_STACK_MAX_WIDTH)]: {
		flexDirection: 'row',
		alignItems: 'stretch',
		flex: 'none',
		width: '100%',
		minWidth: 0,
		gap: '12px',
	},
}))

export const PathTail = styled(Box)(({ theme }) => ({
	flex: '0 0 auto',
	alignSelf: 'flex-start',
	width: 56,
	height: `${NODE_SIZE}px`,
	display: 'flex',
	alignItems: 'center',

	[theme.breakpoints.down(PATH_STACK_MAX_WIDTH)]: {
		display: 'none',
	},
}))

export const PathTailLine = styled(Box)(({ theme }) => ({
	width: '100%',
	height: `${CONNECTOR_WIDTH}px`,
	backgroundImage: DOTTED_LINE(theme),
}))

export const RailRow = styled(Box)(({ theme }) => ({
	position: 'relative',
	width: '100%',
	height: `${NODE_SIZE}px`,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',

	[theme.breakpoints.down(PATH_STACK_MAX_WIDTH)]: {
		flex: `0 0 ${NODE_SIZE}px`,
		width: `${NODE_SIZE}px`,
		height: 'auto',
		alignItems: 'flex-start',
	},
}))

export const ConnectorLeft = styled(Box, {
	shouldForwardProp: prop => prop !== 'color',
})<{ color: ConnectorColor }>(({ theme, color }) => ({
	position: 'absolute',
	top: '50%',
	transform: 'translateY(-50%)',
	left: -1,
	right: '50%',
	height: `${CONNECTOR_WIDTH}px`,
	...connectorStyle(theme, color),

	[theme.breakpoints.down(PATH_STACK_MAX_WIDTH)]: {
		top: 0,
		bottom: 'auto',
		left: '50%',
		right: 'auto',
		transform: 'translateX(-50%)',
		width: `${CONNECTOR_WIDTH}px`,
		height: `${NODE_SIZE / 2}px`,
	},
}))

export const ConnectorRight = styled(Box, {
	shouldForwardProp: prop => prop !== 'color',
})<{ color: ConnectorColor }>(({ theme, color }) => ({
	position: 'absolute',
	top: '50%',
	transform: 'translateY(-50%)',
	left: '50%',
	right: -1,
	height: `${CONNECTOR_WIDTH}px`,
	...connectorStyle(theme, color),

	[theme.breakpoints.down(PATH_STACK_MAX_WIDTH)]: {
		top: `${NODE_SIZE / 2}px`,
		bottom: -1,
		left: '50%',
		right: 'auto',
		transform: 'translateX(-50%)',
		width: `${CONNECTOR_WIDTH}px`,
		height: 'auto',
		...(color === 'dotted'
			? { backgroundImage: DOTTED_LINE_VERTICAL(theme) }
			: {}),
	},
}))

// `picked` adds the blue ring around a selected node. The node itself stays
// purely visual — the click target and hover-grow affordance live on the
// wrapping NodeBadgeButton below, since a native <button> can't be built by
// making this MUI Box polymorphic without losing its style typing.
export const TimelineNode = styled(Box, {
	shouldForwardProp: prop => prop !== 'nodeVariant' && prop !== 'picked',
})<{ nodeVariant: TimelineNodeVariant; picked?: boolean }>(
	({ theme, nodeVariant, picked }) => {
		const base = {
			position: 'relative' as const,
			zIndex: 1,
			width: `${NODE_SIZE}px`,
			height: `${NODE_SIZE}px`,
			borderRadius: '50%',
			boxSizing: 'border-box' as const,
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			flexShrink: 0,
			border: 0,
			backgroundColor: theme.palette.background.paper,
			boxShadow: picked ? `0 0 0 3px rgba(85, 193, 226, 0.35)` : 'none',
			transition: 'box-shadow 0.12s ease',
			'& > svg': { fontSize: '19px' },
		}

		switch (nodeVariant) {
			case 'completed':
				return { ...base, backgroundColor: COMPLETED_GREEN, color: '#fff' }
			case 'current':
				return {
					...base,
					border: `2px solid ${theme.palette.primary.main}`,
					color: theme.palette.primary.main,
				}
			case 'achievement':
				return {
					...base,
					border: `2px solid ${GOLD}`,
					backgroundColor: theme.palette.background.paper,
					color: GOLD,
				}
			default:
				return {
					...base,
					border: `2px solid ${theme.palette.grey[400]}`,
					color: theme.palette.grey[500],
				}
		}
	},
)

// Sized exactly to the node circle so it and the pick indicator position
// consistently regardless of the surrounding row/stacked flex layout. Used
// for nodes that aren't selectable (completed, achievement).
export const NodeBadgeAnchor = styled(Box)({
	position: 'relative',
	width: `${NODE_SIZE}px`,
	height: `${NODE_SIZE}px`,
	flexShrink: 0,
})

// Same footprint as NodeBadgeAnchor, but a real <button> so the whole node
// circle (plus its pick indicator) is the click target for toggling
// selection, and grows slightly on hover as an affordance.
export const NodeBadgeButton = styled('button')({
	position: 'relative',
	width: `${NODE_SIZE}px`,
	height: `${NODE_SIZE}px`,
	flexShrink: 0,
	padding: 0,
	border: 0,
	background: 'none',
	cursor: 'pointer',
	transition: 'transform 0.12s ease',

	'&:hover': {
		transform: 'scale(1.15)',
	},

	'&:focus-visible': {
		outline: '2px solid #55C1E2',
		outlineOffset: '2px',
		borderRadius: '50%',
	},

	'&:disabled': {
		cursor: 'not-allowed',

		'&:hover': {
			transform: 'none',
		},
	},
})

// Purely decorative "selected" marker layered on a node's corner — the whole
// node is the click target (NodeBadgeButton above); this just confirms the
// picked state alongside the node's blue ring.
export const PickIndicator = styled('span')({
	position: 'absolute',
	top: '-4px',
	right: '-4px',
	zIndex: 2,
	width: '16px',
	height: '16px',
	borderRadius: '50%',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	background: '#55C1E2',
	border: '1.5px solid #ffffff',
	color: '#ffffff',

	'& svg': {
		fontSize: '11px',
	},
})

export const TimelineBody = styled(Box)(({ theme }) => ({
	marginTop: '8px',
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: '3px',
	padding: '0 8px',

	[theme.breakpoints.down(PATH_STACK_MAX_WIDTH)]: {
		marginTop: 0,
		padding: '2px 0 24px',
		width: 'auto',
		flex: 1,
		alignItems: 'flex-start',
		textAlign: 'left',
	},
}))

export const NodeTitle = styled(Typography)(({ theme }) => ({
	fontWeight: 500,
	fontSize: '0.85rem',
	lineHeight: 1.3,
	textAlign: 'center',
	display: '-webkit-box',
	WebkitLineClamp: 2,
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',

	[theme.breakpoints.down(PATH_STACK_MAX_WIDTH)]: {
		textAlign: 'left',
		display: 'block',
		WebkitLineClamp: 'unset',
		overflow: 'visible',
	},
}))

export const AchievementTitle = styled(NodeTitle)({
	fontWeight: 700,
	color: GOLD,
})

export const NodeMetaText = styled(Typography)(({ theme }) => ({
	color: theme.palette.text.secondary,
	fontSize: '0.72rem',
	lineHeight: 1.2,
	textAlign: 'center',

	[theme.breakpoints.down(PATH_STACK_MAX_WIDTH)]: {
		textAlign: 'left',
	},
}))

export const VisuallyHidden = styled('span')({
	position: 'absolute',
	width: 1,
	height: 1,
	padding: 0,
	margin: -1,
	overflow: 'hidden',
	clip: 'rect(0, 0, 0, 0)',
	whiteSpace: 'nowrap',
	border: 0,
})

// One onboarding question: a prompt above a wrap of single-choice pills.
export const QuestionGroup = styled(Box)({
	marginBottom: '20px',

	'&:last-of-type': {
		marginBottom: 0,
	},
})

export const QuestionLabel = styled(Typography)({
	fontSize: '0.95rem',
	fontWeight: 600,
	color: 'rgba(0, 0, 0, 0.8)',
	marginBottom: '12px',
})

export const QuestionHint = styled(Typography)({
	fontSize: '0.8rem',
	color: 'rgba(0, 0, 0, 0.6)',
	marginTop: '10px',
})

export const OptionList = styled(Box)({
	display: 'flex',
	flexWrap: 'wrap',
	gap: '10px',
})

export const OptionButton = styled('button', {
	shouldForwardProp: prop => prop !== 'selected',
})<{ selected: boolean }>(({ theme, selected }) => ({
	cursor: 'pointer',
	padding: '9px 16px',
	borderRadius: '999px',
	fontSize: '0.85rem',
	fontWeight: 600,
	lineHeight: 1.3,
	transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease',
	border: `1px solid ${
		selected ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.18)'
	}`,
	background: selected ? theme.palette.primary.main : '#fff',
	color: selected ? '#fff' : 'rgba(0, 0, 0, 0.75)',

	'&:hover': {
		borderColor: theme.palette.primary.main,
		color: selected ? '#fff' : theme.palette.primary.main,
	},

	'&:focus-visible': {
		outline: `2px solid ${LEARN_COLOR}`,
		outlineOffset: 2,
	},
}))

const stepFadeIn = keyframes({
	from: { opacity: 0, transform: 'translateY(6px)' },
	to: { opacity: 1, transform: 'translateY(0)' },
})

// Wraps each step's questions; remounting it (keyed by step id) replays the fade
// so moving between steps animates in.
export const StepFade = styled(Box)({
	minHeight: '220px',
	alignContent: 'start',
	marginBottom: '14px',
	animation: `${stepFadeIn} 0.28s ease`,
})

export const RecommendationBox = styled(Box)({
	position: 'relative',
	marginTop: '16px',
	padding: '14px 18px',
	borderRadius: '12px',
	background: 'rgba(85, 193, 226, 0.08)',
	border: '1px solid rgba(85, 193, 226, 0.3)',
})

// Header row for the box: the "Recommended next" label alongside the
// regenerate control. A flex row (rather than pinning the control absolutely)
// so it can wrap onto its own line instead of overlapping the label now that
// its text is a full sentence ("Regenerate All" / "Replace Selected N")
// instead of a short "All"/count.
export const RecommendationHeaderRow = styled(Box)({
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'space-between',
	flexWrap: 'wrap',
	gap: '8px',
	marginBottom: '10px',
})

// Non-primary regenerate control, so the pathway can be rebuilt without
// competing with the primary Play action. Uses the secondary/outlined skin
// (cyan on white, matching "Edit preferences"): white fill, cyan border +
// icon, hover inverts to filled cyan.
//
// Dual purpose: with no nodes picked it reads "Regenerate All"; with nodes
// picked it reads "Replace Selected {count}" (that batch only).
export const RegenControl = styled('button')({
	display: 'inline-flex',
	alignItems: 'center',
	gap: '4px',
	flexShrink: 0,
	height: '30px',
	padding: '0 10px 0 12px',
	borderRadius: '999px',
	cursor: 'pointer',
	color: '#55C1E2',
	background: '#ffffff',
	border: '1px solid #55C1E2',
	transition: 'background 0.15s ease, color 0.15s ease',

	'& svg': { fontSize: 18 },

	'&:hover': {
		background: '#55C1E2',
		color: '#ffffff',
	},

	'&:focus-visible': {
		outline: '2px solid #55C1E2',
		outlineOffset: '1px',
	},

	'&:disabled, &[aria-disabled="true"]': {
		cursor: 'not-allowed',
		opacity: 0.5,

		'&:hover': {
			background: '#ffffff',
			color: '#55C1E2',
		},
	},
})

export const RegenControlLabel = styled('span')({
	fontSize: '0.72rem',
	fontWeight: 700,
	lineHeight: 1,
	fontVariantNumeric: 'tabular-nums',
})

export const DueChip = styled(Box)({
	display: 'inline-flex',
	alignItems: 'center',
	gap: '5px',
	padding: '3px 10px',
	borderRadius: '999px',
	background: 'rgba(85, 193, 226, 0.12)',
	color: '#2c8fb0',
	fontSize: '0.72rem',
	fontWeight: 600,
	marginBottom: '8px',

	'& svg': {
		fontSize: 15,
	},
})

export const RecTitle = styled(Typography)({
	fontSize: '1.05rem',
	fontWeight: 700,
	color: 'rgba(0, 0, 0, 0.87)',
	marginBottom: '4px',
})

// Footer of the recommendation: primary Play action alongside the secondary
// Regenerate action. Wraps on narrow panels.
export const RecActionRow = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	flexWrap: 'wrap',
	gap: '10px',
	marginTop: '14px',
})

// SecDim's standard button content (mirrors play.secdim.com's #secdimBtn): a
// SecDimButton whose child is this flex row of an icon + a capitalised Averta
// label. Reuse for any branded icon+label button.
export const ButtonContent = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	padding: '0px 14px 0px 9px',
	'& > svg': { fontSize: '1.5rem' },
})

export const ButtonLabel = styled(Typography)({
	margin: '0px 0px 0px 2px',
	lineHeight: 1.7,
	fontSize: '19px',
	fontWeight: 500,
	textTransform: 'capitalize',
	whiteSpace: 'nowrap',
	color: 'inherit',
})

// Primary action: SecDimButton's default filled cyan skin. Root padding + radius
// match the Play standard; the transparent border keeps it identical in the
// border-box model to the outlined secondary below (same footprint — hierarchy is
// carried by fill vs outline, never by size).
export const REC_PLAY_BUTTON_SX = {
	padding: '5px',
	borderRadius: '5px',
	border: '1px solid transparent',
} as const

// Secondary action: same footprint, outlined cyan, hover inverts to filled.
export const REC_REGEN_BUTTON_SX = {
	padding: '5px',
	borderRadius: '5px',
	backgroundColor: 'common.white',
	color: 'primary.main',
	border: '1px solid',
	borderColor: 'primary.main',
	'&:hover': {
		backgroundColor: 'primary.main',
		color: 'common.white',
		borderColor: 'primary.main',
	},
} as const

export const RecJustification = styled(Typography)({
	fontSize: '0.9rem',
	color: 'rgba(0, 0, 0, 0.65)',
	lineHeight: 1.5,
	// Last line in the box — no trailing margin, it only added dead height.
	marginBottom: 0,
})

export const StateMessage = styled(Typography)({
	textAlign: 'center',
	color: 'rgba(0, 0, 0, 0.6)',
	padding: '16px 0',
})

export const SECONDARY_OUTLINE_SX = {
	backgroundColor: 'common.white',
	color: 'primary.main',
	border: '1px solid',
	borderColor: 'primary.main',
	'&:hover': {
		backgroundColor: 'primary.main',
		color: 'common.white',
		borderColor: 'primary.main',
	},
} as const

export const DESTRUCTIVE_OUTLINE_SX = {
	backgroundColor: 'common.white',
	color: 'error.main',
	border: '1px solid',
	borderColor: 'error.main',
	'&:hover': {
		backgroundColor: 'error.main',
		color: 'common.white',
		borderColor: 'error.main',
	},
} as const

// Blocked-by-subscription state: no native `disabled` attribute is used here
// (it would suppress the tooltip explaining why), so the not-allowed cursor
// and dimming have to be applied manually and the hover invert suppressed.
export const DISABLED_REGEN_SX = {
	cursor: 'not-allowed',
	opacity: 0.5,
	'&:hover': {
		backgroundColor: 'primary.main',
		color: 'common.white',
	},
} as const

export const SELECT_MENU_PROPS = {
	MenuProps: { sx: { zIndex: 1600 } },
} as const

export const WizardContent = styled(Box)({
	width: '100%',
	maxWidth: '640px',
	margin: '0 auto',
	flex: 1,
	minHeight: 0,
	display: 'flex',
	flexDirection: 'column',
})

export const WizardHeading = styled(Typography)({
	fontWeight: 600,
	fontSize: '1.25rem',
	lineHeight: 1.2,
	color: 'rgba(0, 0, 0, 0.87)',
	textAlign: 'center',
	marginBottom: '16px',
})

export const WizardFooter = styled(Box)(({ theme }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	gap: '12px',
	marginTop: '20px',
	paddingTop: '16px',
	borderTop: '1px solid rgba(0, 0, 0, 0.08)',

	[theme.breakpoints.down('sm')]: {
		flexDirection: 'column-reverse',
		alignItems: 'stretch',
	},
}))

const wizardProgressSheen = keyframes({
	'0%, 100%': { opacity: 0.5 },
	'50%': { opacity: 1 },
})

export const WizardProgressTrack = styled(Box)({
	position: 'absolute',
	top: 0,
	left: 0,
	width: '100%',
	height: '3px',
	backgroundColor: 'rgba(85, 193, 226, 0.14)',
	zIndex: 3,
	pointerEvents: 'none',
})

export const WizardProgressBar = styled(Box)({
	height: '100%',
	minWidth: '4px',
	background: `linear-gradient(90deg, #3fb3d8 0%, ${LEARN_COLOR} 55%, #8adcf1 100%)`,
	boxShadow:
		'0 0 8px rgba(85, 193, 226, 0.85), 0 0 3px rgba(85, 193, 226, 0.95)',
	borderRadius: '0 2px 2px 0',
	transition: 'width 0.5s cubic-bezier(0.65, 0, 0.35, 1)',
	position: 'relative',

	'&::after': {
		content: '""',
		position: 'absolute',
		top: 0,
		right: 0,
		bottom: 0,
		width: '28px',
		background:
			'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.9) 100%)',
		animation: `${wizardProgressSheen} 1.5s ease-in-out infinite`,
	},
})

export const SummaryCard = styled(Box)({
	border: '1px solid rgba(0, 0, 0, 0.08)',
	borderRadius: '12px',
	overflow: 'hidden',
})

export const SummaryRow = styled(Box)({
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'baseline',
	gap: '2px 20px',
	padding: '10px 16px',
	borderTop: '1px solid rgba(0, 0, 0, 0.07)',

	'&:first-of-type': {
		borderTop: 'none',
	},
})

export const SummaryRowLabel = styled(Typography)({
	flex: '0 0 150px',
	fontSize: '0.8rem',
	fontWeight: 600,
	color: 'rgba(0, 0, 0, 0.5)',
})

export const SummaryRowValue = styled(Typography)({
	flex: '1 1 200px',
	fontSize: '0.9rem',
	color: 'rgba(0, 0, 0, 0.85)',
	lineHeight: 1.45,
})
